import { FC, KeyboardEvent, ReactNode, useRef } from 'react';
import './ViewSwitch.scss';

export interface ViewSwitchOption<T extends string> {
  value: T;
  label: string;
  /** id of the panel this option reveals, for aria-controls */
  panelId: string;
  /** shown instead of the label when variant is "icons"; label becomes the name */
  icon?: ReactNode;
}

interface ViewSwitchProps<T extends string> {
  /** accessible name for the group, e.g. "View" */
  label: string;
  options: ViewSwitchOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** "icons" renders each option's icon with the label as its accessible name */
  variant?: 'text' | 'icons';
}

/**
 * Segmented view switcher.
 *
 * Styled as a pill group, but it keeps tab semantics on purpose: the options
 * still swap sibling panels, so `tablist`/`tab`/`tabpanel` is what actually
 * describes the relationship to assistive tech. Only the visual treatment
 * changes — that is what lets the page drop its second underlined tab bar
 * without the two levels reading as tabs inside tabs.
 *
 * Implements the roving-tabindex keyboard pattern: one stop in the tab order,
 * arrows move between options and wrap, Home/End jump to the ends.
 */
const ViewSwitch = <T extends string,>({ label, options, value, onChange, variant = 'text' }: ViewSwitchProps<T>) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    const next = options[index];
    if (!next || next.value === value) return;
    onChange(next.value);
    refs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        select(index === last ? 0 : index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        select(index === 0 ? last : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        select(0);
        break;
      case 'End':
        event.preventDefault();
        select(last);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`view-switch${variant === 'icons' ? ' view-switch--icons' : ''}`}
      role="tablist"
      aria-label={label}
    >
      {options.map((option, index) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            id={`${option.panelId}-tab`}
            aria-selected={isActive}
            aria-controls={option.panelId}
            tabIndex={isActive ? 0 : -1}
            ref={(el) => { refs.current[index] = el; }}
            className={`view-switch__option${isActive ? ' view-switch__option--active' : ''}`}
            title={variant === 'icons' ? option.label : undefined}
            onClick={() => select(index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {variant === 'icons' && option.icon ? (
              <>
                <span className="view-switch__icon" aria-hidden="true">{option.icon}</span>
                {/* the icon carries no name of its own, so keep the label for
                    screen readers and for getByRole('tab', { name }) queries */}
                <span className="view-switch__sr-only">{option.label}</span>
              </>
            ) : (
              option.label
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ViewSwitch as <T extends string>(props: ViewSwitchProps<T>) => ReturnType<FC>;
